import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { ArrowRight, Upload, X } from "lucide-react";

interface AdminBlogEditorProps {
  postId: string | null; // null = new post
  onClose: () => void;
}

export function AdminBlogEditor({ postId, onClose }: AdminBlogEditorProps) {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [authorName, setAuthorName] = useState('אליס תכשיטים');
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [seoKeywords, setSeoKeywords] = useState('');
  const [isPublished, setIsPublished] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);

  // Fetch existing post
  const { data: existingPost } = useQuery({
    queryKey: ['admin-blog-post', postId],
    queryFn: async () => {
      if (!postId) return null;
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('id', postId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!postId,
  });

  // Fetch tags
  const { data: allTags = [] } = useQuery({
    queryKey: ['blog-tags'],
    queryFn: async () => {
      const { data, error } = await supabase.from('blog_tags').select('*').order('name');
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch post tags
  const { data: postTagIds = [] } = useQuery({
    queryKey: ['admin-post-tags', postId],
    queryFn: async () => {
      if (!postId) return [];
      const { data, error } = await supabase
        .from('blog_post_tags')
        .select('tag_id')
        .eq('post_id', postId);
      if (error) throw error;
      return data?.map(d => d.tag_id) || [];
    },
    enabled: !!postId,
  });

  // Load existing data
  useEffect(() => {
    if (existingPost) {
      setTitle(existingPost.title);
      setSlug(existingPost.slug);
      setExcerpt(existingPost.excerpt || '');
      setContent(existingPost.content || '');
      setCoverImageUrl(existingPost.cover_image_url || '');
      setAuthorName(existingPost.author_name);
      setSeoTitle(existingPost.seo_title || '');
      setSeoDescription(existingPost.seo_description || '');
      setSeoKeywords((existingPost.seo_keywords || []).join(', '));
      setIsPublished(existingPost.is_published);
    }
  }, [existingPost]);

  useEffect(() => {
    if (postTagIds.length > 0) {
      setSelectedTagIds(postTagIds);
    }
  }, [postTagIds]);

  // Auto-generate slug from title
  useEffect(() => {
    if (!postId && title) {
      const generated = title
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^\u0590-\u05FFa-zA-Z0-9-]/g, '')
        .toLowerCase();
      setSlug(generated || title.trim().replace(/\s+/g, '-'));
    }
  }, [title, postId]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('blog-images')
        .upload(fileName, file);
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('blog-images')
        .getPublicUrl(fileName);

      setCoverImageUrl(publicUrl);
      toast({ title: 'התמונה הועלתה בהצלחה' });
    } catch (err: any) {
      toast({ title: 'שגיאה בהעלאת תמונה', description: err.message, variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim() || !slug.trim()) {
      toast({ title: 'נא למלא כותרת', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const keywords = seoKeywords.split(',').map(k => k.trim()).filter(Boolean);

      const postData = {
        title: title.trim(),
        slug: slug.trim(),
        excerpt: excerpt.trim() || null,
        content: content || null,
        cover_image_url: coverImageUrl || null,
        author_name: authorName.trim(),
        seo_title: seoTitle.trim() || null,
        seo_description: seoDescription.trim() || null,
        seo_keywords: keywords,
        is_published: isPublished,
        published_at: isPublished ? (existingPost?.published_at || new Date().toISOString()) : null,
        created_by: session?.user?.id || null,
      };

      let savedPostId = postId;

      if (postId) {
        const { error } = await supabase
          .from('blog_posts')
          .update(postData)
          .eq('id', postId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('blog_posts')
          .insert(postData)
          .select('id')
          .single();
        if (error) throw error;
        savedPostId = data.id;
      }

      // Update tags
      if (savedPostId) {
        // Remove existing tags
        await supabase.from('blog_post_tags').delete().eq('post_id', savedPostId);
        // Add selected tags
        if (selectedTagIds.length > 0) {
          const tagRows = selectedTagIds.map(tagId => ({ post_id: savedPostId!, tag_id: tagId }));
          await supabase.from('blog_post_tags').insert(tagRows);
        }
      }

      queryClient.invalidateQueries({ queryKey: ['admin-blog-posts'] });
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] });
      toast({ title: postId ? 'הפוסט עודכן' : 'הפוסט נוצר בהצלחה' });
      onClose();
    } catch (err: any) {
      toast({ title: 'שגיאה בשמירה', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const toggleTag = (tagId: string) => {
    setSelectedTagIds(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    );
  };

  return (
    <div>
      <button onClick={onClose} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-6">
        <ArrowRight className="h-4 w-4" />
        חזרה לרשימה
      </button>

      <h2 className="text-xl font-bold mb-6">{postId ? 'עריכת פוסט' : 'פוסט חדש'}</h2>

      <div className="space-y-6">
        {/* Title */}
        <div>
          <label className="text-sm font-medium text-foreground mb-1 block">כותרת *</label>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            className="w-full border border-border bg-background px-3 py-2 text-sm"
            placeholder="כותרת הפוסט"
          />
        </div>

        {/* Slug */}
        <div>
          <label className="text-sm font-medium text-foreground mb-1 block">Slug (כתובת URL)</label>
          <input
            value={slug}
            onChange={e => setSlug(e.target.value)}
            className="w-full border border-border bg-background px-3 py-2 text-sm font-mono"
            dir="ltr"
            placeholder="post-slug"
          />
          <p className="text-xs text-muted-foreground mt-1">alice.jewelry/blog/{slug}</p>
        </div>

        {/* Cover Image */}
        <div>
          <label className="text-sm font-medium text-foreground mb-1 block">תמונת שער</label>
          {coverImageUrl ? (
            <div className="relative inline-block">
              <img src={coverImageUrl} alt="שער" className="h-40 object-cover border border-border" />
              <button
                onClick={() => setCoverImageUrl('')}
                className="absolute top-1 left-1 bg-background/80 p-1 hover:bg-background"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <label className="flex items-center gap-2 border border-dashed border-border p-4 cursor-pointer hover:border-primary/50 transition-colors">
              <Upload className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {uploading ? 'מעלה...' : 'העלאת תמונה'}
              </span>
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
            </label>
          )}
        </div>

        {/* Excerpt */}
        <div>
          <label className="text-sm font-medium text-foreground mb-1 block">תקציר</label>
          <textarea
            value={excerpt}
            onChange={e => setExcerpt(e.target.value)}
            className="w-full border border-border bg-background px-3 py-2 text-sm min-h-[80px] resize-y"
            placeholder="תיאור קצר של הפוסט..."
          />
        </div>

        {/* Content */}
        <div>
          <label className="text-sm font-medium text-foreground mb-1 block">תוכן (HTML)</label>
          <textarea
            value={content}
            onChange={e => setContent(e.target.value)}
            className="w-full border border-border bg-background px-3 py-2 text-sm min-h-[300px] resize-y font-mono"
            dir="rtl"
            placeholder="<p>תוכן הפוסט...</p>"
          />
        </div>

        {/* Author */}
        <div>
          <label className="text-sm font-medium text-foreground mb-1 block">שם הכותב</label>
          <input
            value={authorName}
            onChange={e => setAuthorName(e.target.value)}
            className="w-full border border-border bg-background px-3 py-2 text-sm"
          />
        </div>

        {/* Tags */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">תגים</label>
          <div className="flex flex-wrap gap-2">
            {allTags.map(tag => (
              <button
                key={tag.id}
                onClick={() => toggleTag(tag.id)}
                className={`text-sm px-3 py-1 border transition-colors ${
                  selectedTagIds.includes(tag.id)
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-transparent border-border hover:border-primary/50'
                }`}
              >
                {tag.name}
              </button>
            ))}
            {allTags.length === 0 && (
              <p className="text-xs text-muted-foreground">אין תגים. צור תגים מעמוד ניהול הבלוג.</p>
            )}
          </div>
        </div>

        {/* SEO Section */}
        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-semibold mb-4">הגדרות SEO</h3>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">כותרת SEO</label>
              <input
                value={seoTitle}
                onChange={e => setSeoTitle(e.target.value)}
                className="w-full border border-border bg-background px-3 py-2 text-sm"
                placeholder="כותרת מותאמת למנועי חיפוש (אופציונלי)"
              />
              <p className="text-xs text-muted-foreground mt-1">{seoTitle.length}/60 תווים</p>
            </div>

            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">תיאור SEO</label>
              <textarea
                value={seoDescription}
                onChange={e => setSeoDescription(e.target.value)}
                className="w-full border border-border bg-background px-3 py-2 text-sm min-h-[60px] resize-y"
                placeholder="תיאור מותאם למנועי חיפוש (אופציונלי)"
              />
              <p className="text-xs text-muted-foreground mt-1">{seoDescription.length}/160 תווים</p>
            </div>

            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">מילות מפתח</label>
              <input
                value={seoKeywords}
                onChange={e => setSeoKeywords(e.target.value)}
                className="w-full border border-border bg-background px-3 py-2 text-sm"
                placeholder="מפרידים בפסיק: תכשיטים, טבעות אירוסין, זהב"
              />
            </div>
          </div>
        </div>

        {/* Publish toggle */}
        <div className="flex items-center gap-3 border-t border-border pt-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isPublished}
              onChange={e => setIsPublished(e.target.checked)}
              className="w-4 h-4 accent-primary"
            />
            <span className="text-sm font-medium">פרסם מיד</span>
          </label>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'שומר...' : postId ? 'עדכן פוסט' : 'צור פוסט'}
          </Button>
          <Button variant="outline" onClick={onClose}>
            ביטול
          </Button>
        </div>
      </div>
    </div>
  );
}
